import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  Download,
  RefreshCw,
  Search,
  Shuffle,
  UploadCloud,
  Users,
  X,
} from 'lucide-react';

import { ErrorState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { Button } from '@/components/ui/button';
import { useT, useLang } from '@/i18n';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast.store';
import { apiCall } from '@/lib/api-client';
import {
  TIER_META,
  type UploaderProgressSummary,
  type TierName,
} from '@/features/uploader-progress/tierConstants';
import { ADMIN_UPLOADER_PROFILE_KEY } from '@/features/uploader-progress/progressKeys';
import {
  useAdminUploadersProgressOverview,
  type AdminUploaderRow,
} from '@/features/uploader-progress/progressHooks';
import { uploaderApi, type UploaderDetails, type FlagErrorReason } from '@/features/uploader/uploader.api';
import { UploaderScreeningDetailModal } from '@/pages/uploader/components/UploaderScreeningDetailModal';
import { BulkReassignModal } from './components/BulkReassignModal';

/**
 * F5-R4b · «Uploaders v2 — Actividad y cola» — dibujo del CEO (2026-08-28).
 *
 * FUSIÓN de las dos ventanas admin de uploaders en UNA pantalla con conmutador
 * «Actividad de uploaders | Cola de carga» + la ficha por uploader (tercer
 * estado, se abre desde la fila o desde «Abrir su ficha completa»). La ruta
 * vieja /admin/uploader-team redirige acá con ?view=queue (patrón
 * BatchesListPage T6c; AdminShell/AdminChrome NO se tocan en esta tanda).
 *
 * CABLEADO REAL — todo dato pintado sale de un endpoint vivo:
 *   - GET /admin/uploaders-progress-overview        (roster + hoy + mes)
 *   - GET /admin/uploader/team-stats                (cola, atrasados, sin-Medicaid por ventana)
 *   - GET /admin/uploaders/:id/profile-summary      (últimos 7 días, 12 meses, racha)
 *   - GET /admin/uploader/uploaders/:id/details     (30 días, horas de hoy, últimas 20, tiempo medio)
 *   - GET /admin/uploaders-activity/export          (CSV/XLSX; q=email exporta un solo uploader)
 *   - GET /admin/uploader/screenings/:id            (modal «Revisar» — SE REUSA, readOnly, no reclama)
 *
 * RANGOS: el server sólo calcula Hoy · 7 días (corridos) · 30 días (corridos)
 * · Este mes (período NY). Trimestre / Este año / Personalizado del dibujo
 * quedan deshabilitados-con-diagnóstico (NECESITA-SERVER). Lo que no obedece
 * al rango lleva su ventana escrita en el rótulo («sólo hoy», «ahora mismo»,
 * «últimas 20») — doctrina del propio dibujo.
 *
 * NO SE FINGE (NECESITA-SERVER, ver reporte): presencia en vivo por uploader
 * (live_agent_status sólo cubre agentes) · devueltos por rango/uploader ·
 * reclamos vencidos · serie por día fuera de 7d/12m · heatmap día×hora ·
 * cola completa del admin (sólo llega el tramo +24 h) · Medicaid/estado/hogar
 * por fila de la cola · export de cola y export auditado por uploader.
 *
 * CONSERVAS (E-23, controles vivos no dibujados): Actualizar · Reasignar
 * (BulkReassignModal) · «sólo activos» · filtro por nivel (demotado a chips
 * clickeables con su distribución — el dibujo baja los niveles de la cabecera
 * a un chip por fila, y acá viven ambos). MUEREN por el dibujo (declarados):
 * la tira de niveles del header, las flechas de tendencia, el rail lateral de
 * filtros, el donut de Medicaid (baja a KPI) y el drill a ProgressModal (lo
 * reemplaza la ficha, misma data por profile-summary).
 */

type RangeKey = 'hoy' | '7d' | '30d' | 'mes';
type DeadRangeKey = 'trim' | 'anio' | 'custom';
type GranKey = 'dia' | 'mes';

const LIVE_RANGES: readonly RangeKey[] = ['hoy', '7d', '30d', 'mes'] as const;
const DEAD_RANGES: readonly DeadRangeKey[] = ['trim', 'anio', 'custom'] as const;
const QUEUE_PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.charAt(0) ?? '';
  const b = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (a + b).toUpperCase() || '·';
}

function rangeDays(range: RangeKey, now: Date): number {
  switch (range) {
    case 'hoy': return 1;
    case '7d': return 7;
    case '30d': return 30;
    case 'mes': return Math.max(1, now.getDate());
  }
}

/** «4,3 h» — horas con un decimal, en el locale del usuario. */
function fmtHours(h: number, locale: string): string {
  return `${(Math.round(h * 10) / 10).toLocaleString(locale)} h`;
}

function fmtNum(n: number, locale: string): string {
  return n.toLocaleString(locale);
}

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

export function UploadersActivityPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const locale = lang === 'es' ? 'es' : 'en';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // --- estado de vista (URL) -------------------------------------------------
  const view: 'activity' | 'queue' = params.get('view') === 'queue' ? 'queue' : 'activity';
  const fichaId = params.get('u');
  const rangeRaw = params.get('range');
  const range: RangeKey = (LIVE_RANGES as readonly string[]).includes(rangeRaw ?? '')
    ? (rangeRaw as RangeKey)
    : '30d';

  // CONSERVA (E-23): búsqueda + sólo-activos + filtro por nivel, en URL como antes.
  const searchInput = params.get('q') ?? '';
  const activeOnly = params.get('activeOnly') !== 'false';
  const tierFilter = useMemo(
    () => new Set((params.get('tiers')?.split(',').filter(Boolean) ?? []) as TierName[]),
    [params],
  );

  const setParam = (key: string, value: string | null, replace = true): void => {
    setParams(
      (p) => {
        if (value == null || value === '') p.delete(key);
        else p.set(key, value);
        return p;
      },
      { replace },
    );
  };

  const [searchDebounced, setSearchDebounced] = useState(searchInput);
  useEffect(() => {
    const id = window.setTimeout(() => setSearchDebounced(searchInput), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  // --- datos vivos -----------------------------------------------------------
  const overviewQ = useAdminUploadersProgressOverview();
  const teamQ = useQuery({
    queryKey: ['admin', 'uploader-team'],
    queryFn: () => uploaderApi.getAdminTeamStats(),
    refetchInterval: 60_000,
  });

  const roster: AdminUploaderRow[] = useMemo(() => overviewQ.data?.items ?? [], [overviewQ.data]);

  // roster filtrado (lista de actividad) — antes de las queries para que la
  // selección por defecto del panel salga de lo que la lista muestra.
  const filtered: AdminUploaderRow[] = useMemo(() => {
    return roster.filter((row) => {
      if (activeOnly && !row.active) return false;
      if (tierFilter.size > 0 && !tierFilter.has(row.tierDisplayed)) return false;
      if (searchDebounced.trim().length > 0) {
        const q = searchDebounced.toLowerCase();
        if (!row.displayName.toLowerCase().includes(q) && !row.email.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [roster, tierFilter, activeOnly, searchDebounced]);

  // Selección del panel (vista actividad). Default: primera fila visible.
  const [selIdRaw, setSelId] = useState<string | null>(null);
  const selId = selIdRaw ?? filtered[0]?.uploaderId ?? roster[0]?.uploaderId ?? null;
  const panelId = fichaId ?? selId;

  // Perfiles livianos por uploader (sparkline 7d, racha, 12 meses).
  const profileResults = useQueries({
    queries: roster.map((r) => ({
      queryKey: ADMIN_UPLOADER_PROFILE_KEY(Number(r.uploaderId)),
      queryFn: () =>
        apiCall<UploaderProgressSummary>(`/admin/uploaders/${r.uploaderId}/profile-summary`),
      staleTime: 60_000,
      refetchInterval: 120_000,
    })),
  });
  const profileById = useMemo(() => {
    const m = new Map<string, UploaderProgressSummary>();
    roster.forEach((r, i) => {
      const d = profileResults[i]?.data;
      if (d) m.set(r.uploaderId, d);
    });
    return m;
  }, [roster, profileResults]);

  // Details (pesado: 30 días + horas de hoy + últimas 20). Sólo se pide para
  // el uploader seleccionado/ficha, o para todo el roster cuando el rango es
  // 30 días (única fuente server-side de ese número).
  const detailResults = useQueries({
    queries: roster.map((r) => ({
      queryKey: ['admin', 'uploader-details', r.uploaderId],
      queryFn: () => uploaderApi.getAdminUploaderDetails(r.uploaderId),
      enabled: range === '30d' || r.uploaderId === panelId,
      staleTime: 120_000,
    })),
  });
  const detailsById = useMemo(() => {
    const m = new Map<string, UploaderDetails>();
    roster.forEach((r, i) => {
      const d = detailResults[i]?.data;
      if (d) m.set(r.uploaderId, d);
    });
    return m;
  }, [roster, detailResults]);

  // --- «Actualizado hace N s» — real, del dataUpdatedAt de react-query -------
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, []);
  const updatedAt = Math.max(teamQ.dataUpdatedAt || 0, overviewQ.dataUpdatedAt || 0);
  const secsAgo = updatedAt > 0 ? Math.max(0, Math.round((nowTick - updatedAt) / 1000)) : null;

  // --- números del rango -----------------------------------------------------
  const now = new Date();
  const days = rangeDays(range, now);

  const cargasFor = (row: AdminUploaderRow): number | null => {
    switch (range) {
      case 'hoy': return row.uploadsToday;
      case 'mes': return row.uploadsMonth;
      case '7d': {
        const p = teamQ.data?.perUploader.find((x) => x.id === row.uploaderId);
        return p ? p.weekUploaded : null;
      }
      case '30d': {
        const d = detailsById.get(row.uploaderId);
        return d ? d.stats.month.uploaded : null;
      }
    }
  };

  const teamCargas: number | null = useMemo(() => {
    if (roster.length === 0) return null;
    let sum = 0;
    for (const r of roster) {
      const c = cargasFor(r);
      if (c == null) return null; // 30d aún cargando — no se inventa un parcial
      sum += c;
    }
    return sum;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, range, teamQ.data, detailsById]);

  const teamNoMed: number | null = useMemo(() => {
    const tot = teamQ.data?.totals;
    switch (range) {
      case 'hoy': return tot ? tot.noMedicaidToday : null;
      case '7d': return tot ? tot.noMedicaidWeek : null;
      case '30d': return tot ? tot.noMedicaidMonth : null;
      case 'mes': return roster.length ? roster.reduce((a, r) => a + r.noMedicaidUploadsMonth, 0) : null;
    }
  }, [range, teamQ.data, roster]);

  // Tiempo medio del equipo — el server sólo lo calcula PARA HOY (viaja dentro
  // de details/:id). Se toma del primer details cargado.
  const teamAvgToday: number | null = useMemo(() => {
    for (const d of detailsById.values()) return d.teamAvgTimeToUploadHours;
    return null;
  }, [detailsById]);

  const rangeShort = t(`f5r4b.range.${range}` as never).toLowerCase();

  const fmtDay = (d: Date): string =>
    d.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  const rangeNote: string = useMemo(() => {
    if (range === 'hoy') return t('f5r4b.range.note.hoy', { date: fmtDay(now) });
    if (range === 'mes')
      return t('f5r4b.range.note.mes', {
        month: now.toLocaleDateString(locale, { month: 'long' }),
        year: now.getFullYear(),
        n: now.getDate(),
      });
    const from = new Date(now.getTime() - (range === '7d' ? 7 : 30) * 86_400_000);
    return t(`f5r4b.range.note.${range}`, { from: fmtDay(from), to: fmtDay(now) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, locale, nowTick]);

  // --- export (CONSERVA — endpoint vivo /admin/uploaders-activity/export) ----
  const [exportPending, setExportPending] = useState(false);
  const handleExport = async (format: 'csv' | 'xlsx', qOverride?: string): Promise<void> => {
    if (exportPending) return;
    setExportPending(true);
    try {
      const p = new URLSearchParams();
      p.set('format', format);
      if (qOverride != null) {
        p.set('q', qOverride);
      } else {
        if (tierFilter.size > 0) p.set('tiers', Array.from(tierFilter).join(','));
        if (!activeOnly) p.set('activeOnly', 'false');
        if (searchDebounced.trim().length > 0) p.set('q', searchDebounced.trim());
      }
      const accessToken = (await import('@/stores/auth.store')).useAuthStore.getState().accessToken;
      const res = await fetch(`/api/admin/uploaders-activity/export?${p.toString()}`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Export failed (${res.status}): ${text}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disp = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="?([^";]+)"?/.exec(disp);
      a.download = m?.[1] ?? `uploaders-activity-${new Date().toISOString().slice(0, 19)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('uploadersActivity.export.success', { count: qOverride != null ? 1 : filtered.length }));
    } catch (e) {
      toast.error(t('uploadersActivity.export.failed'), (e as Error).message);
    } finally {
      setExportPending(false);
    }
  };

  // --- modales ---------------------------------------------------------------
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // conmutador de vistas: UNA sola llamada a setParams — dos llamadas
  // seguidas con updater funcional se pisan entre sí en react-router.
  const switchView = (v: 'activity' | 'queue'): void => {
    setParams(
      (p) => {
        p.delete('u');
        if (v === 'queue') p.set('view', 'queue');
        else p.delete('view');
        return p;
      },
      { replace: true },
    );
  };

  // --- errores de carga ------------------------------------------------------
  if (overviewQ.error || teamQ.error) {
    const err = (overviewQ.error ?? teamQ.error) as Error;
    return (
      <ErrorState
        icon={<AlertCircle className="w-5 h-5" />}
        title={t('f5r4b.error.title')}
        body={err.message}
        action={{ label: t('uploader.queue.refresh'), onClick: () => { void overviewQ.refetch(); void teamQ.refetch(); } }}
      />
    );
  }

  const isFicha = fichaId != null;
  const title = isFicha
    ? t('f5r4b.title.ficha', { name: detailsById.get(fichaId)?.user.fullName ?? roster.find((r) => r.uploaderId === fichaId)?.displayName ?? '…' })
    : view === 'activity'
      ? t('f5r4b.title.activity')
      : t('f5r4b.title.queue');
  const subtitle = isFicha
    ? t('f5r4b.subtitle.ficha')
    : view === 'activity'
      ? t('f5r4b.subtitle.activity')
      : t('f5r4b.subtitle.queue');

  const busyInitial = overviewQ.isLoading || teamQ.isLoading;

  return (
    <div className="max-w-[1440px] font-body text-nav-navy">
      {/* anuncio accesible del cambio de rango (total nuevo) */}
      <span className="sr-only" aria-live="polite">
        {teamCargas != null ? t('f5r4b.a11y.rangeAnnounce', { range: rangeShort, n: teamCargas }) : ''}
      </span>

      {/* conmutador de vistas — el dibujo fusiona las dos ventanas */}
      <div className="flex items-center gap-2.5 flex-wrap mb-[18px]">
        {/* la ficha pertenece a «Actividad»: el pill queda presionado (dibujo: inActivity) */}
        <ViewPill
          on={isFicha || view === 'activity'}
          label={t('f5r4b.view.activity')}
          onClick={() => switchView('activity')}
        />
        <ViewPill
          on={!isFicha && view === 'queue'}
          label={t('f5r4b.view.queue')}
          onClick={() => switchView('queue')}
        />
      </div>

      <div className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] pb-[30px] overflow-hidden animate-fade-up-slow motion-reduce:animate-none">
        {/* cabecera blanca pegada al tope, con el rango */}
        <div className="bg-white border-b-[1.5px] border-nav-card px-7 py-[22px]">
          <div className="flex items-start justify-between gap-5 flex-wrap mb-[18px]">
            <div className="min-w-0 flex items-start gap-3">
              <UploadCloud className="w-6 h-6 text-nav-tealDark mt-0.5 flex-none" aria-hidden />
              <div className="min-w-0">
                <p className="m-0 font-head text-[20px] font-extrabold tracking-[-0.02em]">{title}</p>
                <p className="m-0 mt-[5px] text-[13.5px] leading-normal text-nav-slate">{subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              {secsAgo != null && (
                <span className="inline-flex items-center gap-2 bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] rounded-full px-3 py-[7px] font-head text-[11.5px] font-bold">
                  <span className="w-2 h-2 rounded-full bg-nav-teal animate-pulse motion-reduce:animate-none" aria-hidden />
                  {t('f5r4b.live.updated', { n: secsAgo })}
                </span>
              )}
              {/* CONSERVA (E-23): refresh manual — el dibujo trae el chip vivo, el botón se conserva */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { void overviewQ.refetch(); void teamQ.refetch(); }}
                disabled={overviewQ.isFetching || teamQ.isFetching}
                aria-label={t('uploader.queue.refresh')}
              >
                <RefreshCw className={cn('w-4 h-4', (overviewQ.isFetching || teamQ.isFetching) && 'animate-spin motion-reduce:animate-none')} />
              </Button>
              {/* CONSERVA (E-23): reasignación masiva de casos del equipo uploader */}
              <Button variant="secondary" size="sm" onClick={() => setBulkOpen(true)}>
                <Shuffle className="w-4 h-4 mr-1" />
                {t('bulkReassign.button')}
              </Button>
              <GhostBtn
                label="CSV"
                disabled={exportPending || (view === 'queue' && !isFicha)}
                title={view === 'queue' && !isFicha ? t('f5r4b.queue.export.diag') : 'CSV'}
                onClick={() => void handleExport('csv')}
              />
              <GhostBtn
                label="XLSX"
                disabled={exportPending || (view === 'queue' && !isFicha)}
                title={view === 'queue' && !isFicha ? t('f5r4b.queue.export.diag') : 'XLSX'}
                onClick={() => void handleExport('xlsx')}
              />
            </div>
          </div>

          {/* fila del rango */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-head text-[10.5px] font-bold uppercase tracking-[0.12em] text-nav-slate">
              {t('f5r4b.range.label')}
            </span>
            <div className="flex items-center gap-0.5 bg-nav-soft border-[1.5px] border-nav-card rounded-[10px] p-[3px] flex-wrap">
              {LIVE_RANGES.map((k) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={range === k}
                  onClick={() => setParam('range', k)}
                  className={cn(
                    'font-head font-bold text-[12.5px] min-h-[44px] px-3.5 rounded-lg transition-colors',
                    range === k ? 'bg-nav-navy text-white hover:bg-nav-navyHover' : 'text-nav-slate hover:bg-white hover:text-nav-navy',
                  )}
                >
                  {t(`f5r4b.range.${k}` as never)}
                </button>
              ))}
              {/* Trimestre / Este año / Personalizado — el server no calcula estos
                  rangos: deshabilitados-con-diagnóstico (NECESITA-SERVER). */}
              {DEAD_RANGES.map((k) => (
                <button
                  key={k}
                  type="button"
                  disabled
                  aria-disabled="true"
                  title={t('f5r4b.range.deadDiag')}
                  className="font-head font-bold text-[12.5px] min-h-[44px] px-3.5 rounded-lg text-nav-faint cursor-not-allowed"
                >
                  {t(`f5r4b.range.${k}` as never)}
                </button>
              ))}
            </div>
            <span className="text-[12.5px] text-nav-slate tabular-nums">{rangeNote}</span>
          </div>
        </div>

        {busyInitial ? (
          <div className="px-7 pt-6 space-y-3">
            <Skeleton className="h-[92px]" />
            <Skeleton className="h-[300px]" />
          </div>
        ) : isFicha ? (
          <FichaView
            row={roster.find((r) => r.uploaderId === fichaId) ?? null}
            details={detailsById.get(fichaId) ?? null}
            profile={profileById.get(fichaId) ?? null}
            roster={roster}
            profileById={profileById}
            range={range}
            days={days}
            rangeShort={rangeShort}
            teamCargas={teamCargas}
            teamAvgToday={teamAvgToday}
            locale={locale}
            onBack={() => setParam('u', null, false)}
            onExport={(email) => void handleExport('csv', email)}
            exportPending={exportPending}
            onTeamCenter={() => navigate('/admin/team')}
            cargas={roster.find((r) => r.uploaderId === fichaId) ? cargasFor(roster.find((r) => r.uploaderId === fichaId) as AdminUploaderRow) : null}
          />
        ) : view === 'activity' ? (
          <ActivityView
            filtered={filtered}
            roster={roster}
            range={range}
            days={days}
            rangeShort={rangeShort}
            rangeNote={rangeNote}
            locale={locale}
            teamCargas={teamCargas}
            teamNoMed={teamNoMed}
            teamAvgToday={teamAvgToday}
            teamStatsFlagged={teamQ.data?.totals.flagged ?? null}
            cargasFor={cargasFor}
            profileById={profileById}
            detailsById={detailsById}
            selId={selId}
            onSelect={(id) => {
              if (selId === id) setParam('u', id, false); // segundo click en la fila ya seleccionada abre la ficha
              else setSelId(id);
            }}
            onOpenFicha={(id) => setParam('u', id, false)}
            searchInput={searchInput}
            onSearch={(v) => setParam('q', v || null)}
            activeOnly={activeOnly}
            onActiveOnly={(v) => setParam('activeOnly', v ? null : 'false')}
            tierFilter={tierFilter}
            onTierToggle={(tier) => {
              const next = new Set(tierFilter);
              if (next.has(tier)) next.delete(tier);
              else next.add(tier);
              setParam('tiers', next.size > 0 ? Array.from(next).join(',') : null);
            }}
            tierDistribution={overviewQ.data?.tierDistribution ?? null}
            onExportOne={(email) => void handleExport('csv', email)}
            exportPending={exportPending}
          />
        ) : (
          <QueueView
            team={teamQ.data ?? null}
            teamCargas={teamCargas}
            teamNoMed={teamNoMed}
            rangeShort={rangeShort}
            locale={locale}
            onReview={(id) => setReviewId(id)}
          />
        )}
      </div>

      {/* «Revisar» del admin: modal REUSADO tal cual, sólo consulta — NO reclama. */}
      {reviewId && (
        <UploaderScreeningDetailModal
          screeningId={reviewId}
          onClose={() => setReviewId(null)}
          readOnly
          source="admin"
        />
      )}

      {/* CONSERVA (E-23): reasignación masiva (audiencia uploader). */}
      <BulkReassignModal
        open={bulkOpen}
        audience="uploader"
        onClose={() => setBulkOpen(false)}
        invalidateQueryKey={['admin', 'uploaders-progress-overview']}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// piezas compartidas
// ---------------------------------------------------------------------------

function ViewPill({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors active:scale-[0.98]',
        on
          ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover hover:border-nav-navyHover'
          : 'bg-white text-nav-navy border-nav-line hover:bg-[#f4fafa] hover:text-nav-tealDark hover:border-nav-teal',
      )}
    >
      {label}
    </button>
  );
}

function GhostBtn({ label, onClick, disabled, title }: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex items-center gap-2 min-h-[44px] px-[15px] rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-navy transition-colors',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark active:scale-[0.98]',
      )}
    >
      <Download className="w-4 h-4" aria-hidden />
      {label}
    </button>
  );
}

function KpiTile({ value, label, sub, tone, title, delay }: {
  value: string | null;
  label: string;
  sub?: string;
  tone?: 'red' | 'amber';
  /** diagnóstico (tooltip) cuando el dato no existe server-side */
  title?: string;
  delay?: number;
}): JSX.Element {
  return (
    <div
      title={title}
      className={cn(
        'bg-white border-[1.5px] rounded-xl px-5 pt-[18px] pb-4 animate-fade-up motion-reduce:animate-none',
        tone === 'red' ? 'border-[#e3b5ae]' : tone === 'amber' ? 'border-nav-amber' : 'border-nav-card',
      )}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {value == null ? (
        <span className="font-head font-extrabold text-[30px] leading-none tracking-[-0.02em] text-nav-faint">—</span>
      ) : (
        <span className={cn(
          'font-head font-extrabold text-[30px] leading-none tracking-[-0.02em] tabular-nums',
          tone === 'red' ? 'text-nav-red' : tone === 'amber' ? 'text-nav-amberText' : 'text-nav-navy',
        )}>
          {value}
        </span>
      )}
      <p className="m-0 mt-[11px] text-[13px] text-nav-slate">{label}</p>
      {sub && (
        <span className="block mt-1.5 font-head text-[11.5px] font-bold text-nav-faint tabular-nums">{sub}</span>
      )}
    </div>
  );
}

function Kicker({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="m-0 mb-3 font-head text-[10.5px] font-bold uppercase tracking-[0.14em] text-nav-teal">
      {children}
    </p>
  );
}

function TierChip({ tier, t }: { tier: TierName; t: ReturnType<typeof useT> }): JSX.Element {
  const meta = TIER_META[tier];
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-head font-bold ring-1 whitespace-nowrap',
      meta.bgClass, meta.ringClass, meta.textClass,
    )}>
      <meta.icon className="w-3 h-3" aria-hidden />
      {t(meta.i18nLabelKey as never)}
    </span>
  );
}

/** Sparkline de N barras (serie real). El dibujo pide 92×34 px. */
function SparkBars({ series, active }: {
  series: Array<{ title: string; value: number }>;
  active: boolean;
}): JSX.Element {
  const max = Math.max(1, ...series.map((s) => s.value));
  return (
    <span className="flex-none flex items-end gap-[2px] w-[92px] h-[34px]" aria-hidden={series.length === 0}>
      {series.map((s, i) => (
        <span
          key={i}
          title={s.title}
          className={cn('flex-1 min-w-0 rounded-t-[2px]', s.value === 0 ? 'bg-nav-hair' : active ? 'bg-nav-teal' : 'bg-[#c9e2e2]')}
          style={{ height: `${Math.max(2, Math.round((s.value / max) * 34))}px`, transition: 'height .3s cubic-bezier(.4,0,.2,1)' }}
        />
      ))}
    </span>
  );
}

/** «hace N min/h/d» — relativo, real. */
function relTime(iso: string, t: ReturnType<typeof useT>): string {
  const diffMin = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (diffMin < 60) return t('f5r4b.rel.min', { n: diffMin });
  if (diffMin < 60 * 24) return t('f5r4b.rel.h', { n: Math.round(diffMin / 60) });
  return t('f5r4b.rel.d', { n: Math.round(diffMin / (60 * 24)) });
}

type RecentRow = UploaderDetails['recentWork'][number];

function outcomeLabel(status: RecentRow['status'], t: ReturnType<typeof useT>): string {
  if (status === 'flagged') return t('f5r4b.mix.flagged');
  if (status === 'no_medicaid') return t('f5r4b.mix.nomed');
  return t('f5r4b.mix.uploaded');
}

// ---------------------------------------------------------------------------
// vista ACTIVIDAD
// ---------------------------------------------------------------------------

interface ActivityViewProps {
  filtered: AdminUploaderRow[];
  roster: AdminUploaderRow[];
  range: RangeKey;
  days: number;
  rangeShort: string;
  rangeNote: string;
  locale: string;
  teamCargas: number | null;
  teamNoMed: number | null;
  teamAvgToday: number | null;
  teamStatsFlagged: number | null;
  cargasFor: (row: AdminUploaderRow) => number | null;
  profileById: Map<string, UploaderProgressSummary>;
  detailsById: Map<string, UploaderDetails>;
  selId: string | null;
  onSelect: (id: string) => void;
  onOpenFicha: (id: string) => void;
  searchInput: string;
  onSearch: (v: string) => void;
  activeOnly: boolean;
  onActiveOnly: (v: boolean) => void;
  tierFilter: Set<TierName>;
  onTierToggle: (tier: TierName) => void;
  tierDistribution: Record<TierName, number> | null;
  onExportOne: (email: string) => void;
  exportPending: boolean;
}

function ActivityView(props: ActivityViewProps): JSX.Element {
  const {
    filtered, roster, range, days, rangeShort, rangeNote, locale,
    teamCargas, teamNoMed, teamAvgToday, teamStatsFlagged,
    cargasFor, profileById, detailsById,
    selId, onSelect, onOpenFicha,
    searchInput, onSearch, activeOnly, onActiveOnly,
    tierFilter, onTierToggle, tierDistribution,
    onExportOne, exportPending,
  } = props;
  const t = useT();

  const noMedPct =
    teamNoMed != null && teamCargas != null && teamCargas > 0
      ? Math.round((teamNoMed / teamCargas) * 1000) / 10
      : null;

  const activeCount = roster.filter((r) => r.active).length;

  return (
    <div className="px-7 pt-6">
      {/* KPIs del equipo — 6 tiles del dibujo */}
      <div className="grid gap-3.5 mb-[22px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))' }}>
        <KpiTile value={teamCargas != null ? fmtNum(teamCargas, locale) : null} label={t('f5r4b.kpi.cargas')} sub={rangeShort} delay={0} />
        <KpiTile
          value={teamCargas != null ? fmtNum(Math.round(teamCargas / days), locale) : null}
          label={t('f5r4b.kpi.promedio')}
          sub={t('f5r4b.kpi.promedio.sub')}
          delay={60}
        />
        <KpiTile
          value={teamAvgToday != null ? fmtHours(teamAvgToday, locale) : null}
          label={t('f5r4b.kpi.tiempo')}
          sub={t('f5r4b.kpi.tiempo.sub')}
          title={t('f5r4b.kpi.tiempo.diag')}
          delay={120}
        />
        <KpiTile
          value={teamStatsFlagged != null ? fmtNum(teamStatsFlagged, locale) : null}
          label={t('f5r4b.kpi.marcados')}
          sub={t('f5r4b.kpi.marcados.sub')}
          tone="red"
          delay={180}
        />
        <KpiTile
          value={teamNoMed != null ? fmtNum(teamNoMed, locale) : null}
          label={t('f5r4b.kpi.sinmed')}
          sub={noMedPct != null ? t('f5r4b.kpi.sinmed.sub', { pct: noMedPct, range: rangeShort }) : rangeShort}
          delay={240}
        />
        {/* Reclamos vencidos — el server no expone el conteo (NECESITA-SERVER) */}
        <KpiTile value={null} label={t('f5r4b.kpi.reclamos')} title={t('f5r4b.kpi.reclamos.diag')} tone="amber" delay={300} />
      </div>

      {/* CONSERVA (E-23) demotada: filtro por nivel + distribución, fuera de la
          cabecera (el dibujo baja los niveles a chip por fila; acá vive el
          filtro que antes era el rail lateral). */}
      {tierDistribution && (
        <div className="flex items-center gap-2 flex-wrap mb-3.5">
          {(['t3', 't2', 't1', 'base', 'below_floor'] as TierName[]).map((tier) => {
            const meta = TIER_META[tier];
            const on = tierFilter.has(tier);
            return (
              <button
                key={tier}
                type="button"
                aria-pressed={on}
                onClick={() => onTierToggle(tier)}
                className={cn(
                  'inline-flex items-center gap-1 px-2.5 min-h-[32px] rounded-full text-[11px] font-head font-bold ring-1 transition-colors',
                  meta.bgClass, meta.ringClass, meta.textClass,
                  on ? 'ring-2 ring-offset-1' : 'opacity-75 hover:opacity-100',
                )}
              >
                <meta.icon className="w-3 h-3" aria-hidden />
                {t(meta.i18nLabelKey as never)}: {tierDistribution[tier]}
              </button>
            );
          })}
          {/* CONSERVA (E-23): sólo activos */}
          <label className="flex items-center gap-1.5 text-xs cursor-pointer ml-2 min-h-[32px]">
            <input type="checkbox" checked={activeOnly} onChange={(e) => onActiveOnly(e.target.checked)} />
            <span className="text-nav-navy">{t('f5r4b.list.activeOnly')}</span>
          </label>
        </div>
      )}

      <div className="flex gap-4 flex-wrap items-start">
        {/* lista de uploaders */}
        <div className="flex-1 min-w-[330px] bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-3.5 flex-wrap px-5 pt-[18px] pb-3.5">
            <p className="m-0 font-head text-[10.5px] font-bold uppercase tracking-[0.14em] text-nav-teal">
              {t('f5r4b.list.kicker', { range: rangeShort })}
            </p>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-nav-faint pointer-events-none" aria-hidden />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t('f5r4b.list.filter.placeholder')}
                aria-label={t('f5r4b.list.filter.aria')}
                className="w-[210px] box-border h-[44px] pl-8 pr-7 rounded-lg border-[1.5px] border-nav-line text-[13px] text-nav-navy bg-white focus:outline-none focus:ring-2 focus:ring-nav-teal"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => onSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-nav-faint hover:text-nav-slate"
                  aria-label={t('common.clear')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-nav-slate border-t border-nav-hair">
              {t('f5r4b.list.empty')}
            </div>
          ) : (
            filtered.map((row, i) => {
              const on = selId === row.uploaderId;
              const cargas = cargasFor(row);
              const profile = profileById.get(row.uploaderId);
              const spark7 = profile?.last7Days ?? null;
              return (
                <div
                  key={row.uploaderId}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(row.uploaderId)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(row.uploaderId); } }}
                  className={cn(
                    'flex items-center gap-3.5 flex-wrap min-h-[80px] px-5 py-3.5 border-t border-nav-hair cursor-pointer transition-colors animate-fade-up motion-reduce:animate-none',
                    on ? 'bg-nav-tealTint border-l-[3px] border-l-nav-teal hover:bg-[#d3e8e9]' : 'border-l-[3px] border-l-transparent bg-white hover:bg-[#f4fafa]',
                    !row.active && 'opacity-70',
                  )}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <span className={cn(
                    'flex-none inline-flex items-center justify-center w-[38px] h-[38px] rounded-full text-white font-head text-[13px] font-bold',
                    row.active ? 'bg-nav-navy' : 'bg-nav-faint',
                  )}>
                    {initialsOf(row.displayName)}
                  </span>

                  <span className="flex-1 min-w-[150px]">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-head text-[13.5px] font-bold">{row.displayName}</span>
                      {/* el nivel baja a chip en la fila (dibujo, «Los niveles se van de la cabecera») */}
                      <TierChip tier={row.tierDisplayed} t={t} />
                      {/* Presencia en vivo: NECESITA-SERVER (live_agent_status sólo
                          cubre agentes). Lo único real acá es cuenta inactiva. */}
                      {!row.active && (
                        <span className="flex-none bg-nav-hair text-nav-slate border border-nav-card rounded-full px-2.5 py-[3px] font-head text-[10.5px] font-bold whitespace-nowrap">
                          {t('f5r4b.row.inactive')}
                        </span>
                      )}
                    </span>
                    <span className="block mt-[3px] text-xs text-nav-slate">{row.email}</span>
                  </span>

                  {/* sparkline: serie real sólo para 7 días (last7Days). Para el
                      resto no hay serie por día server-side → hueco con diagnóstico. */}
                  {range === '7d' && spark7 ? (
                    <SparkBars
                      active={on}
                      series={spark7.map((d) => ({ title: `${d.nyDate} · ${d.uploads}`, value: d.uploads }))}
                    />
                  ) : (
                    <span className="flex-none w-[92px] h-[34px] flex items-end" title={t('f5r4b.diag.spark')} aria-hidden>
                      <span className="w-full h-[2px] bg-nav-hair rounded" />
                    </span>
                  )}

                  <RowMetric value={cargas != null ? fmtNum(cargas, locale) : null} label={t('f5r4b.row.cargas')} />
                  <RowMetric
                    value={cargas != null ? fmtNum(Math.round((cargas / days) * 10) / 10, locale) : null}
                    label={t('f5r4b.row.porDia')}
                  />
                  {/* devueltos por rango/uploader: NECESITA-SERVER */}
                  <RowMetric value={null} label={t('f5r4b.row.devueltos')} tone="red" title={t('f5r4b.diag.devueltos')} />

                  <ChevronRight
                    className="w-4 h-4 text-nav-faint flex-none cursor-pointer"
                    aria-label={t('f5r4b.detail.openFicha')}
                    onClick={(e) => { e.stopPropagation(); onOpenFicha(row.uploaderId); }}
                  />
                </div>
              );
            })
          )}

          <div className="flex items-center justify-between gap-3.5 flex-wrap px-5 py-3.5 border-t border-nav-hair bg-nav-softer">
            <p className="m-0 text-[12.5px] text-nav-slate">{t('f5r4b.list.footHint')}</p>
            <p className="m-0 text-[12.5px] text-nav-slate tabular-nums">{t('f5r4b.list.footActive', { n: activeCount })}</p>
          </div>
        </div>

        {/* panel de detalle (420px, dibujo) */}
        <DetailPanel
          row={roster.find((r) => r.uploaderId === selId) ?? null}
          details={selId ? detailsById.get(selId) ?? null : null}
          profile={selId ? profileById.get(selId) ?? null : null}
          cargas={selId ? (() => { const r = roster.find((x) => x.uploaderId === selId); return r ? cargasFor(r) : null; })() : null}
          days={days}
          rangeNote={rangeNote}
          locale={locale}
          onOpenFicha={onOpenFicha}
          onExportOne={onExportOne}
          exportPending={exportPending}
        />
      </div>
    </div>
  );
}

function RowMetric({ value, label, tone, title }: {
  value: string | null;
  label: string;
  tone?: 'red';
  title?: string;
}): JSX.Element {
  return (
    <span className="flex-none min-w-[62px] text-right" title={title}>
      <span className={cn(
        'block font-head text-[17px] font-extrabold tracking-[-0.01em] tabular-nums',
        value == null ? 'text-nav-faint' : tone === 'red' ? 'text-nav-red' : 'text-nav-navy',
      )}>
        {value ?? '—'}
      </span>
      <span className="block mt-0.5 text-[11px] text-nav-slate">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// panel de detalle (derecha)
// ---------------------------------------------------------------------------

function DetailPanel({ row, details, profile, cargas, days, rangeNote, locale, onOpenFicha, onExportOne, exportPending }: {
  row: AdminUploaderRow | null;
  details: UploaderDetails | null;
  profile: UploaderProgressSummary | null;
  cargas: number | null;
  days: number;
  rangeNote: string;
  locale: string;
  onOpenFicha: (id: string) => void;
  onExportOne: (email: string) => void;
  exportPending: boolean;
}): JSX.Element {
  const t = useT();

  if (!row) {
    return (
      <div className="w-[420px] flex-none min-w-[320px] bg-white border-[1.5px] border-nav-card rounded-xl p-8 text-center text-sm text-nav-slate">
        {t('f5r4b.detail.pick')}
      </div>
    );
  }

  const avgToday = details && details.stats.today.uploaded > 0 ? details.stats.today.avgTimeToUploadHours : null;

  // ventana de horas: 7–17 como el dibujo, más cualquier hora con cargas
  // reales fuera de esa franja (no se esconde trabajo).
  const hourly = details?.hourlyUploadsToday ?? null;
  const hourWindow: Array<{ hour: number; count: number }> = hourly
    ? hourly.filter((h) => (h.hour >= 7 && h.hour <= 17) || h.count > 0)
    : [];
  const hourMax = Math.max(1, ...hourWindow.map((h) => h.count));

  // mix real: cómo terminaron sus últimas 20 revisiones (recentWork)
  const recent = details?.recentWork ?? [];
  const mixDefs: Array<{ key: RecentRow['status']; label: string; cls: string }> = [
    { key: 'uploaded', label: t('f5r4b.mix.uploaded'), cls: 'bg-nav-teal' },
    { key: 'no_medicaid', label: t('f5r4b.mix.nomed'), cls: 'bg-nav-amber' },
    { key: 'flagged', label: t('f5r4b.mix.flagged'), cls: 'bg-[#e3b5ae]' },
  ];
  const mix = mixDefs
    .map((m) => ({ ...m, n: recent.filter((r) => r.status === m.key).length }))
    .filter((m) => m.n > 0 || recent.length === 0);
  const mixTotal = Math.max(1, mix.reduce((a, m) => a + m.n, 0));

  return (
    <div className="w-[420px] flex-none min-w-[320px] bg-white border-[1.5px] border-nav-card rounded-xl px-[22px] py-5">
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="min-w-0">
          <p className="m-0 mb-1 font-head text-[17px] font-extrabold tracking-[-0.01em]">{row.displayName}</p>
          <p className="m-0 text-[12.5px] text-nav-slate">{row.email}</p>
        </div>
        <TierChip tier={row.tierDisplayed} t={t} />
      </div>

      <p className="my-2.5 mb-[18px] px-3 py-2 bg-nav-soft rounded-lg text-xs text-nav-slate tabular-nums">{rangeNote}</p>

      <div className="grid gap-3 mb-[18px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
        <StatBox label={t('f5r4b.detail.stat.cargas')} value={cargas != null ? fmtNum(cargas, locale) : null} />
        <StatBox
          label={t('f5r4b.detail.stat.porDia')}
          value={cargas != null ? fmtNum(Math.round((cargas / days) * 10) / 10, locale) : null}
        />
        <StatBox label={t('f5r4b.detail.stat.tiempo')} value={avgToday != null ? fmtHours(avgToday, locale) : null} title={t('f5r4b.kpi.tiempo.diag')} />
        {/* devueltos por rango: NECESITA-SERVER */}
        <StatBox label={t('f5r4b.detail.stat.devueltos')} value={null} tone="red" title={t('f5r4b.diag.devueltos')} />
        <StatBox label={t('f5r4b.detail.stat.sinmed')} value={fmtNum(row.noMedicaidUploadsMonth, locale)} />
        <StatBox
          label={t('f5r4b.detail.stat.racha')}
          value={profile ? t('f5r4b.detail.racha.dias', { n: profile.streak.daysWorking }) : null}
        />
      </div>

      <Kicker>{t('f5r4b.detail.hours.kicker')}</Kicker>
      {hourWindow.length > 0 ? (
        <>
          <div className="flex items-end gap-1 h-[70px] mb-2">
            {hourWindow.map((h) => (
              <span key={h.hour} title={t('f5r4b.detail.hours.title', { h: h.hour, n: h.count })} className="flex-1 min-w-0 flex items-end h-full">
                <span
                  className={cn('block w-full rounded-t-[3px]', h.count === 0 ? 'bg-nav-hair' : 'bg-nav-teal')}
                  style={{ height: `${Math.max(2, Math.round((h.count / hourMax) * 70))}px`, transition: 'height .3s cubic-bezier(.4,0,.2,1)' }}
                />
              </span>
            ))}
          </div>
          <div className="flex justify-between mb-5">
            <span className="text-[10.5px] text-nav-faint tabular-nums">{hourWindow[0]?.hour}:00</span>
            <span className="text-[10.5px] text-nav-faint tabular-nums">{hourWindow[Math.floor(hourWindow.length / 2)]?.hour}:00</span>
            <span className="text-[10.5px] text-nav-faint tabular-nums">{hourWindow[hourWindow.length - 1]?.hour}:00</span>
          </div>
        </>
      ) : (
        <Skeleton className="h-[70px] mb-5" />
      )}

      <Kicker>{t('f5r4b.detail.mix.kicker')}</Kicker>
      <div className="flex h-3 rounded-md overflow-hidden bg-nav-hair mb-3.5">
        {mix.map((m) => (
          <div key={m.key} title={`${m.label}: ${m.n}`} className={m.cls} style={{ width: `${(m.n / mixTotal) * 100}%`, transition: 'width .3s cubic-bezier(.4,0,.2,1)' }} />
        ))}
      </div>
      <div className="flex flex-col gap-2 mb-5">
        {mix.map((m) => (
          <div key={m.key} className="flex items-center gap-2.5">
            <span className={cn('flex-none w-[11px] h-[11px] rounded-[3px]', m.cls)} aria-hidden />
            <span className="flex-1 min-w-0 text-[13px]">{m.label}</span>
            <span className="flex-none font-head text-[13px] font-bold tabular-nums">{m.n}</span>
          </div>
        ))}
      </div>

      <Kicker>{t('f5r4b.detail.recent.kicker')}</Kicker>
      <div className="flex flex-col gap-2 mb-[18px]">
        {recent.length === 0 ? (
          <p className="m-0 text-[12.5px] text-nav-slate">{t('f5r4b.detail.recent.empty')}</p>
        ) : (
          recent.slice(0, 4).map((r) => (
            <div key={r.id} className="flex items-center gap-2.5 min-h-[52px] px-3 py-2 bg-nav-softer border-[1.5px] border-nav-card rounded-[10px]">
              <span className={cn(
                'flex-none w-2 h-2 rounded-full',
                r.status === 'flagged' ? 'bg-nav-red' : r.status === 'no_medicaid' ? 'bg-nav-amber' : 'bg-nav-sage',
              )} aria-hidden />
              <span className="flex-1 min-w-0">
                <span className="block font-head text-[12.5px] font-bold truncate">{r.clientLastName}</span>
                <span className="block mt-0.5 text-[11.5px] text-nav-slate tabular-nums">
                  CIN ···{r.clientCinLast6} · {relTime(r.processedAt, t)}
                  {r.timeToProcessHours != null ? ` · ${fmtHours(r.timeToProcessHours, locale)}` : ''}
                </span>
              </span>
              <span className={cn(
                'flex-none max-w-[44%] text-right font-head text-[11px] font-bold',
                r.status === 'flagged' ? 'text-nav-red' : 'text-nav-slate',
              )}>
                {outcomeLabel(r.status, t)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* disclaimer fijo del dibujo — respaldo real: motivationalOnly del server */}
      <div className="flex items-start gap-2.5 bg-nav-softer border-[1.5px] border-nav-card rounded-[10px] px-[15px] py-[13px] text-[12.5px] leading-relaxed text-nav-slate">
        <AlertCircle className="w-4 h-4 flex-none mt-0.5" aria-hidden />
        <span>{t('f5r4b.detail.note')}</span>
      </div>

      <div className="flex gap-2.5 flex-wrap mt-4">
        <button
          type="button"
          onClick={() => onOpenFicha(row.uploaderId)}
          className="inline-flex items-center min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-navy bg-nav-navy text-white font-head text-[12.5px] font-bold hover:bg-nav-navyHover hover:border-nav-navyHover transition-colors active:scale-[0.98]"
        >
          {t('f5r4b.detail.openFicha')}
        </button>
        <GhostBtn label={t('f5r4b.detail.export')} disabled={exportPending} onClick={() => onExportOne(row.email)} />
      </div>
    </div>
  );
}

function StatBox({ label, value, tone, title }: {
  label: string;
  value: string | null;
  tone?: 'red';
  title?: string;
}): JSX.Element {
  return (
    <div
      title={title}
      className={cn(
        'rounded-[10px] border-[1.5px] px-3.5 py-3',
        tone === 'red' ? 'bg-nav-redTint border-[#e3b5ae]' : 'bg-nav-softer border-nav-card',
      )}
    >
      <span className={cn(
        'block font-head text-[9.5px] font-bold tracking-[0.1em] uppercase',
        tone === 'red' ? 'text-nav-red' : 'text-nav-faint',
      )}>
        {label}
      </span>
      <span className={cn(
        'block mt-[5px] font-head text-[18px] font-extrabold tracking-[-0.01em] tabular-nums',
        value == null ? 'text-nav-faint' : tone === 'red' ? 'text-nav-red' : 'text-nav-navy',
      )}>
        {value ?? '—'}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FICHA del uploader
// ---------------------------------------------------------------------------

function FichaView({ row, details, profile, roster, profileById, range, days, rangeShort, teamCargas, teamAvgToday, locale, onBack, onExport, exportPending, onTeamCenter, cargas }: {
  row: AdminUploaderRow | null;
  details: UploaderDetails | null;
  profile: UploaderProgressSummary | null;
  roster: AdminUploaderRow[];
  profileById: Map<string, UploaderProgressSummary>;
  range: RangeKey;
  days: number;
  rangeShort: string;
  teamCargas: number | null;
  teamAvgToday: number | null;
  locale: string;
  onBack: () => void;
  onExport: (email: string) => void;
  exportPending: boolean;
  onTeamCenter: () => void;
  cargas: number | null;
}): JSX.Element {
  const t = useT();
  const [gran, setGran] = useState<GranKey>(range === 'mes' ? 'mes' : 'dia');
  const [logFilter, setLogFilter] = useState<'todas' | 'ok' | 'err' | 'sinmed'>('todas');

  // TODOS los hooks antes de cualquier return temprano (row puede llegar null
  // mientras el roster carga — el conteo de hooks no puede cambiar entre renders).

  // serie del gráfico — SÓLO granularidades con serie real server-side:
  // día (last7Days) y mes (history12). Semana/Año: NECESITA-SERVER.
  const series: Array<{ tick: string; value: number }> = useMemo(() => {
    if (gran === 'dia') {
      return (profile?.last7Days ?? []).map((d) => {
        const dt = new Date(`${d.nyDate}T12:00:00`);
        return { tick: dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' }), value: d.uploads };
      });
    }
    return (profile?.history12 ?? [])
      .slice()
      .sort((a, b) => a.periodYear - b.periodYear || a.periodMonth - b.periodMonth)
      .map((h) => ({
        tick: new Date(h.periodYear, h.periodMonth - 1, 1).toLocaleDateString(locale, { month: 'short' }),
        value: h.totalUploads,
      }));
  }, [gran, profile, locale]);

  // «Contra el promedio del equipo» — días activos, ventana real de 7 días
  const teamActiveDays7 = useMemo(() => {
    const vals: number[] = [];
    for (const r of roster) {
      const p = profileById.get(r.uploaderId);
      if (p) vals.push(p.last7Days.filter((d) => d.uploads > 0).length);
    }
    if (vals.length === 0) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  }, [roster, profileById]);

  // devoluciones por motivo — ventana real: sus últimas 20 revisiones
  const reasonCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of (details?.recentWork ?? []).filter((x) => x.status === 'flagged')) {
      const key = (r.flaggedError ?? 'other') as FlagErrorReason | string;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [details]);

  if (!row) {
    return (
      <div className="px-7 pt-6">
        <BackLink onClick={onBack} label={t('f5r4b.ficha.back')} />
        <div className="mt-4"><Skeleton className="h-[300px]" /></div>
      </div>
    );
  }

  const name = details?.user.fullName ?? row.displayName;
  const email = details?.user.email ?? row.email;
  const lastLogin = details?.user.lastLoginAt
    ? t('f5r4b.ficha.lastLogin', { date: new Date(details.user.lastLoginAt).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) })
    : details
      ? t('f5r4b.ficha.lastLogin.never')
      : '';

  const porDia = cargas != null ? Math.round((cargas / days) * 10) / 10 : null;
  const teamPorDia = teamCargas != null && roster.length > 0
    ? Math.round((teamCargas / roster.length / days) * 10) / 10
    : null;
  const avgToday = details && details.stats.today.uploaded > 0 ? details.stats.today.avgTimeToUploadHours : null;

  const sMax = Math.max(1, ...series.map((s) => s.value));
  const sTotal = series.reduce((a, b) => a + b.value, 0);
  const unit = t(`f5r4b.vol.unit.${gran}` as never);

  const myActiveDays7 = profile ? profile.last7Days.filter((d) => d.uploads > 0).length : null;

  const reasonMax = Math.max(1, ...reasonCounts.map(([, n]) => n));
  const reasonLabel = (key: string): string => {
    const known: FlagErrorReason[] = ['no_medicaid', 'data_inconsistent', 'cin_invalid', 'duplicate', 'other'];
    return known.includes(key as FlagErrorReason) ? t(`f5r4b.err.${key}` as never) : key;
  };

  // sus cargas (últimas 20) + filtros del dibujo
  const recent = details?.recentWork ?? [];
  const logRows = recent.filter((r) => {
    if (logFilter === 'ok') return r.status === 'uploaded';
    if (logFilter === 'err') return r.status === 'flagged';
    if (logFilter === 'sinmed') return r.status === 'no_medicaid';
    return true;
  });

  return (
    <div className="px-7 pt-[22px]">
      <BackLink onClick={onBack} label={t('f5r4b.ficha.back')} />

      <div className="flex items-start justify-between gap-5 flex-wrap mt-[18px] mb-[22px]">
        <div className="flex items-center gap-4 min-w-0">
          <span className={cn(
            'flex-none inline-flex items-center justify-center w-14 h-14 rounded-full text-white font-head text-[18px] font-bold',
            row.active ? 'bg-nav-navy' : 'bg-nav-faint',
          )}>
            {initialsOf(name)}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap mb-[5px]">
              <p className="m-0 font-head text-[24px] font-extrabold tracking-[-0.02em]">{name}</p>
              <TierChip tier={row.tierDisplayed} t={t} />
              {!row.active && (
                <span className="flex-none bg-nav-hair text-nav-slate border border-nav-card rounded-full px-2.5 py-[3px] font-head text-[10.5px] font-bold whitespace-nowrap">
                  {t('f5r4b.row.inactive')}
                </span>
              )}
            </div>
            {/* «en el equipo desde…» del dibujo: el server no expone la fecha de
                alta (NECESITA-SERVER) — lo real es el último acceso. */}
            <p className="m-0 text-[13px] text-nav-slate">{email}{lastLogin ? ` · ${lastLogin}` : ''}</p>
          </div>
        </div>
        <div className="flex gap-2.5 flex-wrap">
          <GhostBtn label={t('f5r4b.ficha.export')} disabled={exportPending} onClick={() => onExport(email)} />
          <GhostBtn label={t('f5r4b.ficha.team')} onClick={onTeamCenter} />
        </div>
      </div>

      {/* 6 KPIs de la ficha */}
      <div className="grid gap-3.5 mb-[22px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <KpiTile value={cargas != null ? fmtNum(cargas, locale) : null} label={t('f5r4b.detail.stat.cargas')} sub={rangeShort} delay={0} />
        <KpiTile
          value={porDia != null ? fmtNum(porDia, locale) : null}
          label={t('f5r4b.detail.stat.porDia')}
          sub={teamPorDia != null ? t('f5r4b.ficha.kpi.equipo', { n: fmtNum(teamPorDia, locale) }) : undefined}
          delay={60}
        />
        <KpiTile
          value={avgToday != null ? fmtHours(avgToday, locale) : null}
          label={t('f5r4b.detail.stat.tiempo')}
          sub={teamAvgToday != null ? t('f5r4b.cmp.equipo', { n: fmtHours(teamAvgToday, locale) }) : t('f5r4b.kpi.tiempo.sub')}
          title={t('f5r4b.kpi.tiempo.diag')}
          delay={120}
        />
        <KpiTile value={null} label={t('f5r4b.detail.stat.devueltos')} tone="red" title={t('f5r4b.diag.devueltos')} delay={180} />
        <KpiTile value={null} label={t('f5r4b.kpi.reclamos')} tone="amber" title={t('f5r4b.kpi.reclamos.diag')} delay={240} />
        <KpiTile
          value={profile ? fmtNum(profile.streak.daysWorking, locale) : null}
          label={t('f5r4b.detail.stat.racha')}
          sub={t('f5r4b.ficha.kpi.rachaSub')}
          delay={300}
        />
      </div>

      {/* su volumen en el tiempo */}
      <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px] mb-4">
        <div className="flex items-center justify-between gap-3.5 flex-wrap mb-[18px]">
          <div className="min-w-0">
            <p className="m-0 mb-1 font-head text-[10.5px] font-bold uppercase tracking-[0.14em] text-nav-teal">
              {t('f5r4b.vol.kicker')}
            </p>
            <p className="m-0 text-[12.5px] text-nav-slate tabular-nums">{t(`f5r4b.gran.note.${gran}` as never)}</p>
          </div>
          <div className="flex items-center gap-0.5 bg-nav-soft border-[1.5px] border-nav-card rounded-[10px] p-[3px]">
            {(['dia', 'mes'] as GranKey[]).map((g) => (
              <button
                key={g}
                type="button"
                aria-pressed={gran === g}
                onClick={() => setGran(g)}
                className={cn(
                  'font-head font-bold text-[12.5px] min-h-[44px] px-3.5 rounded-lg transition-colors',
                  gran === g ? 'bg-nav-navy text-white hover:bg-nav-navyHover' : 'text-nav-slate hover:bg-white hover:text-nav-navy',
                )}
              >
                {t(`f5r4b.gran.${g}` as never)}
              </button>
            ))}
            {/* Semana / Año: el server no guarda esas series (NECESITA-SERVER) */}
            {(['semana', 'anio'] as const).map((g) => (
              <button
                key={g}
                type="button"
                disabled
                aria-disabled="true"
                title={t('f5r4b.gran.deadDiag')}
                className="font-head font-bold text-[12.5px] min-h-[44px] px-3.5 rounded-lg text-nav-faint cursor-not-allowed"
              >
                {t(`f5r4b.gran.${g}` as never)}
              </button>
            ))}
          </div>
        </div>

        {series.length === 0 ? (
          <Skeleton className="h-[150px]" />
        ) : (
          <div className="flex items-end gap-1.5 h-[170px]">
            {series.map((p, i) => (
              <span key={i} title={t('f5r4b.vol.bar.title', { tick: p.tick, n: p.value })} className="flex-1 min-w-0 flex flex-col justify-end h-full">
                <span className="block mb-1.5 text-center font-head text-[10px] font-bold text-nav-faint tabular-nums whitespace-nowrap overflow-hidden">
                  {fmtNum(p.value, locale)}
                </span>
                <span
                  className={cn('block rounded-t-[3px]', p.value === 0 ? 'bg-nav-hair' : p.value < sMax * 0.35 ? 'bg-nav-faint' : 'bg-nav-teal')}
                  style={{ height: `${Math.max(3, Math.round((p.value / sMax) * 100))}px`, transition: 'height .3s cubic-bezier(.4,0,.2,1)' }}
                />
                <span className="block mt-2 text-center text-[10px] text-nav-faint tabular-nums whitespace-nowrap overflow-hidden">
                  {p.tick}
                </span>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-[26px] flex-wrap mt-5 pt-[18px] border-t border-nav-hair">
          <FootStat value={fmtNum(sTotal, locale)} label={t('f5r4b.vol.total')} />
          <FootStat value={series.length > 0 ? fmtNum(Math.round(sTotal / series.length), locale) : '—'} label={t('f5r4b.vol.prom', { unit })} />
          <FootStat value={series.length > 0 ? fmtNum(sMax, locale) : '—'} label={t('f5r4b.vol.mejor', { unit })} />
          <FootStat value={fmtNum(series.filter((s) => s.value === 0).length, locale)} label={t('f5r4b.vol.sinAct', { unit })} />
        </div>
      </div>

      <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {/* contra el promedio del equipo */}
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
          <Kicker>{t('f5r4b.cmp.kicker')}</Kicker>
          <div className="flex flex-col gap-3.5">
            <CompareRow
              label={t('f5r4b.cmp.cargasDia')}
              mine={porDia}
              team={teamPorDia}
              higherIsBetter
              locale={locale}
              t={t}
            />
            <CompareRow
              label={t('f5r4b.cmp.tiempo')}
              mine={avgToday}
              team={teamAvgToday}
              higherIsBetter={false}
              locale={locale}
              fmt={(n) => fmtHours(n, locale)}
              t={t}
            />
            {/* devueltos por rango: NECESITA-SERVER — fila con diagnóstico */}
            <div title={t('f5r4b.diag.devueltos')}>
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <span className="text-[13px]">{t('f5r4b.cmp.devueltos')}</span>
                <span className="font-head text-[12.5px] font-bold text-nav-faint">—</span>
              </div>
              <div className="h-[10px] rounded-[5px] bg-nav-hair" />
            </div>
            <CompareRow
              label={t('f5r4b.cmp.dias')}
              mine={myActiveDays7}
              team={teamActiveDays7}
              higherIsBetter
              locale={locale}
              t={t}
            />
          </div>
        </div>

        {/* por qué le devolvieron cargas */}
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
          <Kicker>{t('f5r4b.err.kicker')}</Kicker>
          {reasonCounts.length === 0 ? (
            <p className="m-0 text-[12.5px] text-nav-slate">{details ? t('f5r4b.err.empty') : ''}</p>
          ) : (
            <div className="flex flex-col gap-[11px]">
              {reasonCounts.map(([key, n]) => (
                <div key={key}>
                  <div className="flex items-baseline justify-between gap-3 mb-[5px]">
                    <span className="text-[13px]">{reasonLabel(key)}</span>
                    <span className="font-head text-[13px] font-bold tabular-nums">{n}</span>
                  </div>
                  <div className="h-1.5 rounded-[3px] bg-nav-hair overflow-hidden">
                    <div className="h-1.5 rounded-[3px] bg-[#c0705f]" style={{ width: `${Math.round((n / reasonMax) * 100)}%`, transition: 'width .3s cubic-bezier(.4,0,.2,1)' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* cuándo trabaja — heatmap día×hora: NECESITA-SERVER (sección con diagnóstico) */}
      <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px] mb-4">
        <Kicker>{t('f5r4b.heat.kicker')}</Kicker>
        <p className="m-0 text-[12.5px] leading-relaxed text-nav-slate">{t('f5r4b.heat.diag')}</p>
      </div>

      {/* sus cargas — últimas 20 (recentWork), filtrables */}
      <div className="bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
        <div className="flex items-center justify-between gap-3.5 flex-wrap px-[22px] pt-5 pb-3.5">
          <p className="m-0 font-head text-[10.5px] font-bold uppercase tracking-[0.14em] text-nav-teal">
            {t('f5r4b.log.kicker')}
          </p>
          <div className="flex gap-2 flex-wrap">
            {(['todas', 'ok', 'err', 'sinmed'] as const).map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={logFilter === f}
                onClick={() => setLogFilter(f)}
                className={cn(
                  'min-h-[44px] px-3.5 rounded-lg border-[1.5px] font-head text-[12px] font-bold transition-colors',
                  logFilter === f
                    ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover'
                    : 'bg-white text-nav-navy border-nav-line hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark',
                )}
              >
                {t(`f5r4b.log.f.${f}` as never)}
              </button>
            ))}
          </div>
        </div>

        {logRows.map((r, i) => (
          <div
            key={r.id}
            className="flex items-center gap-3.5 flex-wrap min-h-[64px] px-[22px] py-2.5 border-t border-nav-hair bg-white animate-fade-up motion-reduce:animate-none"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <span className={cn(
              'flex-none w-[9px] h-[9px] rounded-full',
              r.status === 'flagged' ? 'bg-nav-red' : r.status === 'no_medicaid' ? 'bg-nav-amber' : 'bg-nav-sage',
            )} aria-hidden />
            <span className="flex-1 min-w-[160px]">
              <span className="block font-head text-[13.5px] font-bold">{r.clientLastName}</span>
              <span className="block mt-0.5 text-xs text-nav-slate tabular-nums">
                CIN ···{r.clientCinLast6}{r.agentName ? ` · ${r.agentName}` : ''} · {new Date(r.processedAt).toLocaleString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            </span>
            <span className="flex-none w-[58px] text-right font-head text-[12.5px] font-bold text-nav-slate tabular-nums">
              {r.timeToProcessHours != null ? fmtHours(r.timeToProcessHours, locale) : '—'}
            </span>
            <span className={cn(
              'flex-none w-[160px] text-right font-head text-[12px] font-bold',
              r.status === 'flagged' ? 'text-nav-red' : r.status === 'no_medicaid' ? 'text-nav-amberText' : 'text-nav-slate',
            )}>
              {r.status === 'flagged' && r.flaggedError
                ? `${t('f5r4b.mix.flagged')} · ${reasonLabel(r.flaggedError)}`
                : outcomeLabel(r.status, t)}
            </span>
          </div>
        ))}
        {details && logRows.length === 0 && (
          <div className="px-[22px] py-6 border-t border-nav-hair text-center text-sm text-nav-slate">
            {t('f5r4b.detail.recent.empty')}
          </div>
        )}

        <div className="flex items-center justify-between gap-3.5 flex-wrap px-[22px] py-3.5 border-t border-nav-hair bg-nav-softer">
          <p className="m-0 text-[12.5px] text-nav-slate tabular-nums">{t('f5r4b.log.page', { n: logRows.length })}</p>
          {/* export auditado por uploader: NECESITA-SERVER */}
          <GhostBtn label={t('f5r4b.log.export')} disabled title={t('f5r4b.log.export.diag')} />
        </div>
      </div>
    </div>
  );
}

function BackLink({ onClick, label }: { onClick: () => void; label: string }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 min-h-[44px] pl-2.5 pr-3.5 rounded-lg font-head text-[13px] font-bold text-nav-teal hover:bg-nav-tealTint hover:text-nav-tealDark transition-colors"
    >
      <ArrowLeft className="w-4 h-4" aria-hidden />
      {label}
    </button>
  );
}

function FootStat({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <span>
      <span className="block font-head text-[22px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">{value}</span>
      <span className="block mt-1.5 text-[12.5px] text-nav-slate">{label}</span>
    </span>
  );
}

function CompareRow({ label, mine, team, higherIsBetter, locale, fmt, t }: {
  label: string;
  mine: number | null;
  team: number | null;
  higherIsBetter: boolean;
  locale: string;
  fmt?: (n: number) => string;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  if (mine == null || team == null) {
    return (
      <div>
        <div className="flex items-baseline justify-between gap-3 mb-1.5">
          <span className="text-[13px]">{label}</span>
          <span className="font-head text-[12.5px] font-bold text-nav-faint">—</span>
        </div>
        <div className="h-[10px] rounded-[5px] bg-nav-hair" />
      </div>
    );
  }
  const pct = Math.round(((mine - team) / Math.max(team, 0.01)) * 100);
  const good = higherIsBetter ? pct >= 0 : pct <= 0;
  const scale = Math.max(mine, team, 0.01) * 1.35;
  const show = fmt ?? ((n: number) => fmtNum(n, locale));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-[13px]">{label}</span>
        <span className={cn('font-head text-[12.5px] font-bold tabular-nums', good ? 'text-nav-sageText' : 'text-nav-amberText')}>
          {pct > 0 ? '+' : ''}{pct}%
        </span>
      </div>
      <div className="relative h-[10px] rounded-[5px] bg-nav-hair">
        <div
          className={cn('h-[10px] rounded-[5px]', good ? 'bg-nav-teal' : 'bg-nav-amber')}
          style={{ width: `${Math.min(100, (mine / scale) * 100)}%`, transition: 'width .3s cubic-bezier(.4,0,.2,1)' }}
        />
        <span
          className="absolute -top-[3px] -bottom-[3px] w-[2px] rounded-[1px] bg-nav-navy"
          style={{ left: `${Math.min(100, (team / scale) * 100)}%` }}
          aria-hidden
        />
      </div>
      <div className="flex items-baseline justify-between gap-3 mt-1.5">
        <span className="font-head text-[12px] font-bold tabular-nums text-nav-navy">{show(mine)}</span>
        <span className="text-[12px] text-nav-slate tabular-nums">{t('f5r4b.cmp.equipo', { n: show(team) })}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// vista COLA
// ---------------------------------------------------------------------------

type TeamStats = NonNullable<Awaited<ReturnType<typeof uploaderApi.getAdminTeamStats>>>;

function QueueView({ team, teamCargas, teamNoMed, rangeShort, locale, onReview }: {
  team: TeamStats | null;
  teamCargas: number | null;
  teamNoMed: number | null;
  rangeShort: string;
  locale: string;
  onReview: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const [agingFilter, setAgingFilter] = useState<'all' | 'd1a3' | 'gt3'>('all');
  const [page, setPage] = useState(0);

  const totals = team?.totals ?? null;
  const overdue = useMemo(() => team?.overdueScreenings ?? [], [team]);

  const oldestH = overdue.reduce((a, r) => Math.max(a, r.hoursSinceSubmit), 0);
  const noMedPct =
    teamNoMed != null && teamCargas != null && teamCargas > 0
      ? Math.round((teamNoMed / teamCargas) * 1000) / 10
      : null;

  // antigüedad — tramos REALES: el server separa pendiente vs +24 h, y los
  // +24 h vienen listados (top-50 más viejos) con sus horas. El corte fino
  // <4 h / 4-24 h del dibujo pide backend (NECESITA-SERVER).
  const lt24 = totals ? Math.max(0, totals.pending - totals.pending24hPlus) : 0;
  const d1a3 = overdue.filter((r) => r.hoursSinceSubmit >= 24 && r.hoursSinceSubmit < 72).length;
  const gt3 = overdue.filter((r) => r.hoursSinceSubmit >= 72).length;
  const agingTotal = Math.max(1, lt24 + d1a3 + gt3);
  const agingSegs: Array<{ key: 'lt24' | 'd1a3' | 'gt3'; n: number; cls: string; clickable: boolean }> = [
    { key: 'lt24', n: lt24, cls: 'bg-nav-sage', clickable: false },
    { key: 'd1a3', n: d1a3, cls: 'bg-nav-amber', clickable: true },
    { key: 'gt3', n: gt3, cls: 'bg-[#c0705f]', clickable: true },
  ];

  // de dónde viene lo atrasado — por agente, del listado real de atrasados
  const sources = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of overdue) m.set(r.agentName, (m.get(r.agentName) ?? 0) + 1);
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [overdue]);
  const srcMax = Math.max(1, ...sources.map(([, n]) => n));

  // tabla: atrasados +24 h (lo único que el server le lista al admin),
  // filtrable por tramo y paginada de a 25
  const tableRows = useMemo(() => {
    if (agingFilter === 'd1a3') return overdue.filter((r) => r.hoursSinceSubmit >= 24 && r.hoursSinceSubmit < 72);
    if (agingFilter === 'gt3') return overdue.filter((r) => r.hoursSinceSubmit >= 72);
    return overdue;
  }, [overdue, agingFilter]);
  const pages = Math.max(1, Math.ceil(tableRows.length / QUEUE_PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const pageRows = tableRows.slice(safePage * QUEUE_PAGE_SIZE, (safePage + 1) * QUEUE_PAGE_SIZE);

  if (!team || !totals) {
    return (
      <div className="px-7 pt-6 space-y-3">
        <Skeleton className="h-[92px]" />
        <Skeleton className="h-[260px]" />
      </div>
    );
  }

  return (
    <div className="px-7 pt-6">
      {/* 5 KPIs de la cola */}
      <div className="grid gap-3.5 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))' }}>
        <KpiTile value={fmtNum(totals.pending, locale)} label={t('f5r4b.q.pendientes')} sub={t('f5r4b.q.ahora')} delay={0} />
        <KpiTile
          value={fmtNum(totals.pending24hPlus, locale)}
          label={t('f5r4b.q.atrasados')}
          sub={overdue.length > 0 ? t('f5r4b.q.atrasados.sub', { h: Math.round(oldestH) }) : t('f5r4b.q.ahora')}
          tone={totals.pending24hPlus > 0 ? 'red' : undefined}
          delay={60}
        />
        <KpiTile
          value={fmtNum(totals.flagged, locale)}
          label={t('f5r4b.kpi.marcados')}
          sub={t('f5r4b.kpi.marcados.sub')}
          tone={totals.flagged > 0 ? 'amber' : undefined}
          delay={120}
        />
        <KpiTile value={teamCargas != null ? fmtNum(teamCargas, locale) : null} label={t('f5r4b.q.subidos')} sub={rangeShort} delay={180} />
        <KpiTile
          value={teamNoMed != null ? fmtNum(teamNoMed, locale) : null}
          label={t('f5r4b.kpi.sinmed')}
          sub={noMedPct != null ? t('f5r4b.kpi.sinmed.sub', { pct: noMedPct, range: rangeShort }) : rangeShort}
          delay={240}
        />
      </div>

      <div className="grid gap-3.5 mb-[22px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {/* antigüedad de lo pendiente */}
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-[22px] py-5">
          <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
            <p className="m-0 font-head text-[10.5px] font-bold uppercase tracking-[0.14em] text-nav-teal">
              {t('f5r4b.aging.kicker')}
            </p>
            <span className="text-[12.5px] text-nav-slate tabular-nums">{t('f5r4b.aging.enCola', { n: totals.pending })}</span>
          </div>
          <div className="flex h-3.5 rounded-[7px] overflow-hidden bg-nav-hair mb-4">
            {agingSegs.map((s) => (
              <div
                key={s.key}
                title={t('f5r4b.aging.seg.title', { name: t(`f5r4b.aging.${s.key}` as never), n: s.n })}
                className={s.cls}
                style={{ width: `${(s.n / agingTotal) * 100}%`, transition: 'width .3s cubic-bezier(.4,0,.2,1)' }}
              />
            ))}
          </div>
          <div className="flex flex-col gap-[9px]">
            {agingSegs.map((s) => (
              <button
                key={s.key}
                type="button"
                disabled={!s.clickable}
                aria-pressed={s.clickable ? agingFilter === s.key : undefined}
                title={s.clickable ? undefined : t('f5r4b.aging.lt24.diag')}
                onClick={() => {
                  if (!s.clickable) return;
                  setAgingFilter((cur) => (cur === s.key ? 'all' : (s.key as 'd1a3' | 'gt3')));
                  setPage(0);
                }}
                className={cn(
                  'flex items-center gap-2.5 w-full box-border min-h-[44px] px-2.5 py-1.5 rounded-lg transition-colors text-left',
                  s.clickable ? 'hover:bg-[#f4fafa] cursor-pointer' : 'cursor-not-allowed opacity-70',
                  s.clickable && agingFilter === s.key && 'bg-nav-tealTint',
                )}
              >
                <span className={cn('flex-none w-[11px] h-[11px] rounded-[3px]', s.cls)} aria-hidden />
                <span className="flex-1 min-w-0 text-[13px]">{t(`f5r4b.aging.${s.key}` as never)}</span>
                <span className="flex-none font-head text-[13px] font-bold tabular-nums">{s.n}</span>
              </button>
            ))}
          </div>
        </div>

        {/* de dónde viene lo atrasado */}
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-[22px] py-5">
          <Kicker>{t('f5r4b.src.kicker')}</Kicker>
          {sources.length === 0 ? (
            <p className="m-0 text-[12.5px] text-nav-slate">{t('f5r4b.src.empty')}</p>
          ) : (
            <>
              <div className="flex flex-col gap-[11px]">
                {sources.map(([agent, n]) => (
                  <div key={agent}>
                    <div className="flex items-baseline justify-between gap-3 mb-[5px]">
                      <span className="text-[13px]">{agent}</span>
                      <span className="font-head text-[13px] font-bold tabular-nums">{n}</span>
                    </div>
                    <div className="h-1.5 rounded-[3px] bg-nav-hair overflow-hidden">
                      <div className="h-1.5 rounded-[3px] bg-nav-amber" style={{ width: `${Math.round((n / srcMax) * 100)}%`, transition: 'width .3s cubic-bezier(.4,0,.2,1)' }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="m-0 mt-4 pt-3.5 border-t border-nav-hair text-[12.5px] leading-relaxed text-nav-slate">
                {t('f5r4b.src.note', { total: overdue.length, agents: sources.length })}
              </p>
            </>
          )}
        </div>
      </div>

      {/* pestañas de la cola del dibujo — el server no le expone al admin la
          lista completa (sólo el tramo +24 h): deshabilitadas-con-diagnóstico,
          con los conteos que SÍ son reales (NECESITA-SERVER la lista). */}
      <div className="flex items-center gap-2.5 flex-wrap mb-3.5">
        {([
          ['pendientes', totals.pending],
          ['marcados', totals.flagged],
          ['subidos', teamCargas],
          ['todos', null],
          ['hogar', null],
        ] as Array<[string, number | null]>).map(([key, count]) => (
          <button
            key={key}
            type="button"
            disabled
            aria-disabled="true"
            title={t('f5r4b.tabs.diag')}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-faint cursor-not-allowed"
          >
            {t(`f5r4b.tabs.${key}` as never)}
            {count != null && <span className="tabular-nums font-semibold">{fmtNum(count, locale)}</span>}
          </button>
        ))}
      </div>

      {/* tabla de atrasados — con espera y estado (lo real del payload) */}
      <div className="bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-2.5 bg-white border-b border-nav-hair">
          <p className="m-0 font-head text-[10.5px] font-bold uppercase tracking-[0.14em] text-nav-teal">
            {t('f5r4b.table.scope')}
          </p>
        </div>
        <div className="flex items-center gap-3.5 px-5 h-10 bg-nav-softer border-b border-nav-hair">
          <span className="flex-none min-w-[76px] font-head text-[10.5px] font-bold uppercase tracking-[0.1em] text-nav-slate">{t('f5r4b.col.espera')}</span>
          <span className="flex-1 min-w-[150px] font-head text-[10.5px] font-bold uppercase tracking-[0.1em] text-nav-slate">{t('f5r4b.col.cliente')}</span>
          <span className="flex-none w-[78px] font-head text-[10.5px] font-bold uppercase tracking-[0.1em] text-nav-slate" title={t('f5r4b.col.med.diag')}>{t('f5r4b.col.med')}</span>
          <span className="flex-none w-[96px] font-head text-[10.5px] font-bold uppercase tracking-[0.1em] text-nav-slate" title={t('f5r4b.col.estado.diag')}>{t('f5r4b.col.estado')}</span>
          <span className="w-[88px] flex-none" />
        </div>

        {pageRows.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-nav-sageText bg-nav-sageTint/40">
            {t('f5r4b.table.empty')}
          </div>
        ) : (
          pageRows.map((r, i) => (
            <div
              key={r.id}
              className={cn(
                'flex items-center gap-3.5 flex-wrap min-h-[68px] px-5 py-[11px] bg-white animate-fade-up motion-reduce:animate-none',
                i > 0 && 'border-t border-nav-hair',
              )}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <span className="flex-none inline-flex items-center justify-center min-w-[76px] min-h-[44px] px-2.5 rounded-lg bg-nav-redTint border border-[#e3b5ae] text-nav-red font-head text-[12.5px] font-bold tabular-nums whitespace-nowrap">
                {(Math.round(r.hoursSinceSubmit * 10) / 10).toLocaleString(locale)} h
              </span>

              <span className="flex-1 min-w-[150px]">
                <span className="font-head text-[13.5px] font-bold">{r.clientLastName}</span>
                <span className="block mt-[3px] text-xs text-nav-slate tabular-nums">
                  CIN ···{r.clientCinLast6} · {r.agentName} · {new Date(r.submittedAt).toLocaleString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>

              {/* Medicaid / estado por fila: el payload de atrasados no los trae — NECESITA-SERVER */}
              <span className="flex-none w-[78px] font-head text-[12px] font-bold text-nav-faint" title={t('f5r4b.col.med.diag')}>—</span>
              <span className="flex-none w-[96px] font-head text-[12px] font-bold text-nav-faint" title={t('f5r4b.col.estado.diag')}>—</span>

              <button
                type="button"
                onClick={() => onReview(r.id)}
                className="flex-none w-[88px] justify-center inline-flex items-center min-h-[44px] rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-navy hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark transition-colors active:scale-[0.98]"
              >
                {t('f5r4b.row.revisar')}
              </button>
            </div>
          ))
        )}

        <div className="flex items-center justify-between gap-3.5 flex-wrap px-5 py-3.5 border-t border-nav-hair bg-nav-softer">
          <p className="m-0 text-[12.5px] text-nav-slate tabular-nums">
            {t('f5r4b.table.page', { shown: pageRows.length, total: tableRows.length, p: safePage + 1, tp: pages })}
          </p>
          <Pager
            page={safePage}
            pages={pages}
            onPage={setPage}
            prevLabel={t('f5r4b.pager.prev')}
            nextLabel={t('f5r4b.pager.next')}
            pageLabel={t('f5r4b.pager.pageOf', { p: safePage + 1, tp: pages })}
            className="mt-0"
          />
        </div>
      </div>

      <p className="m-0 mt-4 px-4 py-3.5 bg-white border-[1.5px] border-nav-card rounded-[10px] text-[12.5px] leading-relaxed text-nav-slate flex items-start gap-2.5">
        <Users className="w-4 h-4 flex-none mt-0.5" aria-hidden />
        <span>{t('f5r4b.queue.note')}</span>
      </p>
    </div>
  );
}
